import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';

export type KoobTableSortDirection = 'asc' | 'desc';
export type KoobTableSort = {
  key: string;
  direction: KoobTableSortDirection;
} | null;

/** none → asc → desc → none, the standard column-header toggle cycle. */
export const getNextSort = (
  sort: KoobTableSort,
  key: string,
): KoobTableSort => {
  if (sort?.key !== key) return { key, direction: 'asc' };
  if (sort.direction === 'asc') return { key, direction: 'desc' };
  return null;
};

type NoClassName<T> = Omit<T, 'className'>;

// `grid` turns the table into a dense data grid: dividers on both axes.
// The selectors live on the <table> so nested rows/cells need no props.
// Specificity is deliberate — `[&_tbody>tr:last-child]` (0,2,2) has to beat
// KoobTableBody's `[&_tr:last-child]:border-0` (0,2,1), and the two
// `tbody:…-child` rules (0,3,2) then beat that one in turn.
const tableVariants = cva('w-full caption-bottom text-sm', {
  variants: {
    variant: {
      default: '',
      grid: [
        // Explicit: without it the borders model is `separate`, so adjacent
        // cell/row borders stack instead of merging and every divider paints
        // at double width.
        'border-collapse',
        // column dividers (never trailing, the card border closes the row)
        '[&_th]:border-solid [&_th]:border-border [&_td]:border-solid [&_td]:border-border',
        '[&_th:not(:last-child)]:border-r [&_td:not(:last-child)]:border-r',
        // every row keeps its divider, including the last one of each tbody
        '[&_tbody>tr:last-child]:border-b [&_tbody>tr:last-child]:border-solid [&_tbody>tr:last-child]:border-border',
        // a tbody is a block (e.g. a rowSpan'ed group): close it with a
        // darker — not thicker — rule, so the block reads above the rows
        // inside it without a heavy line cutting the grid in half
        '[&_tbody:not(:last-child)>tr:last-child]:border-b-gray-300',
        // …except the very last one, where the card border is the closer
        '[&_tbody:last-child>tr:last-child]:border-b-0',
      ].join(' '),
    },
  },
  defaultVariants: { variant: 'default' },
});

type KoobTableProps = NoClassName<React.TableHTMLAttributes<HTMLTableElement>> &
  VariantProps<typeof tableVariants> & {
    /** Rendered inside the table card, below the scroll container — stays
     *  put when the table scrolls horizontally (e.g. a pagination bar). */
    footerBar?: React.ReactNode;
    /** Rendered inside the table card, above the scroll container (e.g. a
     *  section title with its filters), so title and table share one card. */
    headerBar?: React.ReactNode;
    /** Drops the table's own card (border, radius) for a table that sits
     *  inside another card, which would otherwise draw a second border. */
    embedded?: boolean;
  };

const KoobTable = React.forwardRef<HTMLTableElement, KoobTableProps>(
  ({ footerBar, headerBar, variant, embedded = false, ...props }, ref) => (
    <div
      className={cn(
        // clip, not hidden: it rounds the corners without becoming a scroll
        // box, so the toolbar can stick to the page's own scroll.
        'w-full overflow-clip bg-card',
        embedded
          ? // take the card's bottom radius, or the square box paints over
            // its rounded corners; inset the outer columns like the card's
            // own header, so the table lines up with the card title above it
            'rounded-b-[inherit] [&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-5 [&_td:last-child]:pr-5'
          : // No shadow: pages often wrap tables in a scroll box, which would
            // crop it at the corners. The border draws the edge.
            'rounded-2xl border border-solid border-k-gray',
      )}
    >
      {headerBar && (
        // The toolbar stays in view while a long table scrolls under it,
        // below the app's fixed navbar if it has one (--koob-sticky-top).
        <div className="sticky top-[var(--koob-sticky-top,0px)] z-raised bg-white">
          {headerBar}
        </div>
      )}
      <div className="relative w-full overflow-auto">
        <table ref={ref} className={tableVariants({ variant })} {...props} />
      </div>
      {footerBar}
    </div>
  ),
);
KoobTable.displayName = 'KoobTable';

export const KoobTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  NoClassName<React.HTMLAttributes<HTMLTableSectionElement>>
>((props, ref) => (
  <thead
    ref={ref}
    className="[&_tr]:bg-navy-50 [&_tr:hover]:bg-navy-50"
    {...props}
  />
));
KoobTableHeader.displayName = 'KoobTableHeader';

export const KoobTableBody = React.forwardRef<
  HTMLTableSectionElement,
  NoClassName<React.HTMLAttributes<HTMLTableSectionElement>>
>((props, ref) => (
  <tbody
    ref={ref}
    className="[&_tr:nth-child(even)]:bg-[#FAFBFC] [&_tr:last-child]:border-0"
    {...props}
  />
));
KoobTableBody.displayName = 'KoobTableBody';

export const KoobTableFooter = React.forwardRef<
  HTMLTableSectionElement,
  NoClassName<React.HTMLAttributes<HTMLTableSectionElement>>
>((props, ref) => (
  <tfoot
    ref={ref}
    className="border-t border-solid border-border bg-muted/50 font-medium [&_tr:last-child]:border-0"
    {...props}
  />
));
KoobTableFooter.displayName = 'KoobTableFooter';

type KoobTableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  selected?: boolean;
};

export const KoobTableRow = React.forwardRef<
  HTMLTableRowElement,
  KoobTableRowProps
>(({ selected, onClick, className, ...props }, ref) => (
  <tr
    ref={ref}
    data-state={selected ? 'selected' : undefined}
    onClick={onClick}
    className={cn(
      'border-b border-solid border-[#F0F2F5] transition-colors data-[state=selected]:!bg-navy-50',
      onClick && 'cursor-pointer hover:!bg-navy-50',
      className,
    )}
    {...props}
  />
));
KoobTableRow.displayName = 'KoobTableRow';

const tableHead = cva(
  'h-11 px-3 first:pl-5 last:pr-5 align-middle text-xs font-semibold uppercase tracking-[0.04em] text-k-p whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
    },
    defaultVariants: { align: 'left' },
  },
);

const SortIcon = ({ direction }: { direction?: KoobTableSortDirection }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={cn(
      'h-3.5 w-3.5 shrink-0',
      direction ? 'text-brand' : 'text-muted-foreground/50',
    )}
  >
    {direction === 'asc' && (
      <>
        <path d="m5 12 7-7 7 7" />
        <path d="M12 19V5" />
      </>
    )}
    {direction === 'desc' && (
      <>
        <path d="M12 5v14" />
        <path d="m19 12-7 7-7-7" />
      </>
    )}
    {!direction && (
      <>
        <path d="m21 16-4 4-4-4" />
        <path d="M17 20V4" />
        <path d="m3 8 4-4 4 4" />
        <path d="M7 4v16" />
      </>
    )}
  </svg>
);

type KoobTableHeadProps = NoClassName<
  React.ThHTMLAttributes<HTMLTableCellElement>
> &
  VariantProps<typeof tableHead> & {
    /** Current sort direction of this column; null/undefined when not sorted. */
    sortDirection?: KoobTableSortDirection | null;
    /** Presence makes the column sortable (renders the sort icon + click). */
    onSort?: () => void;
  };

export const KoobTableHead = React.forwardRef<
  HTMLTableCellElement,
  KoobTableHeadProps
>(({ align, sortDirection, onSort, children, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    onClick={onSort}
    aria-sort={
      sortDirection
        ? sortDirection === 'asc'
          ? 'ascending'
          : 'descending'
        : undefined
    }
    className={cn(tableHead({ align }), onSort && 'cursor-pointer select-none')}
    {...props}
  >
    {onSort ? (
      <div
        className={cn(
          'flex items-center gap-1.5',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
        )}
      >
        {children}
        <SortIcon direction={sortDirection ?? undefined} />
      </div>
    ) : (
      children
    )}
  </th>
));
KoobTableHead.displayName = 'KoobTableHead';

const tableCell = cva(
  'px-3 py-4 first:pl-5 last:pr-5 align-middle text-k-h [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
    },
    defaultVariants: { align: 'left' },
  },
);

type KoobTableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> &
  VariantProps<typeof tableCell>;

export const KoobTableCell = React.forwardRef<
  HTMLTableCellElement,
  KoobTableCellProps
>(({ align, className, ...props }, ref) => (
  <td ref={ref} className={cn(tableCell({ align }), className)} {...props} />
));
KoobTableCell.displayName = 'KoobTableCell';

export const KoobTableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  NoClassName<React.HTMLAttributes<HTMLTableCaptionElement>>
>((props, ref) => (
  <caption
    ref={ref}
    className="mt-4 text-sm text-muted-foreground"
    {...props}
  />
));
KoobTableCaption.displayName = 'KoobTableCaption';

export default KoobTable;
